'use client';

import { useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Box, Button, Container, TextField, Card, Alert, IconButton, InputAdornment } from '@mui/material';
import { useBoolean } from 'minimal-shared/hooks';
import { Iconify } from 'src/components/iconify';
import { useAuthContext } from '@/src/auth/hooks';
import { CONFIG } from '@/src/global-config';
import { paths } from 'src/routes/paths';
import { EmailRedanRegistreradFel } from 'src/lib/localAuth';
import { SignUpTerms } from '../components/sign-up-terms';

export function LocalRegistreraView() {
  const router = useRouter();
  // LocalAuthProvider exposes register/login beyond the shared AuthContextValue type.
  const { register } = useAuthContext() as any;
  // Inloggningen skickar hit med ?email= när adressen saknar konto — fyll i den åt användaren
  // i stället för att be om samma adress en gång till.
  const searchParams = useSearchParams();
  const epostFrånInloggningen = searchParams.get('email') || '';
  const [email, setEmail] = useState(epostFrånInloggningen);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const showPassword = useBoolean();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Goes through LocalAuthProvider.register so context state (user,
      // authenticated) and localStorage (accessToken) are updated — calling
      // registerLocal() directly here would skip all of that.
      await register(email, password);
      // Redirect home after successful registration (there is no standalone /dashboard route)
      router.push(CONFIG.auth.redirectPath);
    } catch (err: any) {
      if (err instanceof EmailRedanRegistreradFel) {
        router.push(`${paths.LOGGA_IN}?email=${encodeURIComponent(email)}`);
        return;
      }
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 12 }}>
      <Card sx={{ p: 4 }}>
        <h1>Skapa lokalt konto</h1>

        {epostFrånInloggningen && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Det finns inget lokalt konto för <strong>{epostFrånInloggningen}</strong> ännu. Skapa
            det här så loggas du in direkt.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleRegister} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Lösenord"
            type={showPassword.value ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={showPassword.onToggle}
                      edge="end"
                      aria-label={showPassword.value ? 'Dölj lösenord' : 'Visa lösenord'}
                    >
                      <Iconify
                        icon={showPassword.value ? 'solar:eye-bold' : 'solar:eye-closed-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
          >
            {loading ? 'Skapar...' : 'Skapa konto'}
          </Button>
        </Box>

        <SignUpTerms />

        <Box sx={{ mt: 2, textAlign: 'center', fontSize: '12px', color: 'text.secondary' }}>
          ✅ Använd lokal autentisering (ingen Firebase)
        </Box>
      </Card>
    </Container>
  );
}
